'use client';

import clsx from 'clsx';
import { useEffect, useState } from 'react';

import { GAME_NUMBERS, GAME_SCORE_MAX, MATCH_RETIRE_ACTIONS, MATCH_RETIRE_OPTIONS } from '@/components/constants/constants';
import { MatchSide } from '@/components/constants/types';
import {
	ConfirmForfeitMatchModal,
	ConfirmRetireMatchModal,
	MatchRetireDropdown,
	ConfirmUndoForfeitModal,
	ConfirmUndoRetirementModal
} from '@/components/molecules';
import { MatchupStatusEnum } from '@/entities/matchup/constants';
import { usePermissionStore } from '@/store/permission-store';

interface GameScoreInputProps {
	gameNumber: string;
	initialRetireStatus?: string;
	initialValue?: number;
	isLocked?: boolean;
	matchId: string;
	scoreFormat: number;
	onChange: (value: number, retireStatus: string) => void;
	showDropdown?: boolean;
	team: MatchSide;
	matchWinner: boolean;
	matchupStatus?: MatchupStatusEnum;
	games?: { max: number; winBy: number }[];
	opponentScore?: number | null;
}

const getDropdownOptions = (initialRetireStatus: string) => {
	if (initialRetireStatus === 'FF') {
		return [
			{ value: MATCH_RETIRE_ACTIONS.RETIRE, label: MATCH_RETIRE_ACTIONS.RETIRE },
			{ value: MATCH_RETIRE_ACTIONS.UNDO_FORFEIT, label: MATCH_RETIRE_ACTIONS.UNDO_FORFEIT }
		];
	} else if (initialRetireStatus === 'RET') {
		return [
			{ value: MATCH_RETIRE_ACTIONS.UNDO_RETIRE, label: MATCH_RETIRE_ACTIONS.UNDO_RETIRE },
			{ value: MATCH_RETIRE_ACTIONS.FORFEIT, label: MATCH_RETIRE_ACTIONS.FORFEIT }
		];
	} else if (initialRetireStatus === 'WD') {
		return [
			{ value: MATCH_RETIRE_ACTIONS.UNDO_RETIRE, label: MATCH_RETIRE_ACTIONS.UNDO_RETIRE },
			{ value: MATCH_RETIRE_ACTIONS.UNDO_FORFEIT, label: MATCH_RETIRE_ACTIONS.UNDO_FORFEIT }
		];
	}

	return MATCH_RETIRE_OPTIONS;
};

export const GameScoreInput = ({
	gameNumber,
	onChange,
	initialValue = 0,
	initialRetireStatus = '',
	isLocked = false,
	matchId,
	scoreFormat,
	showDropdown = true,
	team,
	matchWinner,
	matchupStatus,
	games
}: GameScoreInputProps) => {
	const permissionStore = usePermissionStore();
	const [score, setScore] = useState<number | null>(initialValue ?? 0);
	const [showRetireModal, setShowRetireModal] = useState<boolean>(false);
	const [showForfeitModal, setShowForfeitModal] = useState<boolean>(false);
	const [showUndoRetireModal, setShowUndoRetireModal] = useState<boolean>(false);
	const [showUndoForfeitModal, setShowUndoForfeitModal] = useState<boolean>(false);
	const isShowDropdown = !isLocked && gameNumber !== GAME_NUMBERS.FINAL && showDropdown;
	const isMatchWinner = matchWinner && matchupStatus !== MatchupStatusEnum.SCHEDULED_MATCHUP_STATUS;
	const isFinalGame = gameNumber === GAME_NUMBERS.FINAL && matchupStatus !== MatchupStatusEnum.SCHEDULED_MATCHUP_STATUS;
	//const isReadyToBeStartedMatchupStatus = matchupStatus === MatchupStatusEnum.READY_TO_BE_STARTED_STATUS;

	useEffect(() => {
		setScore(initialValue);
	}, [initialValue]);

	const handleRetireSelect = (value: string | number) => {
		if (value === MATCH_RETIRE_ACTIONS.RETIRE) {
			setShowRetireModal(true);
		} else if (value === MATCH_RETIRE_ACTIONS.FORFEIT) {
			setShowForfeitModal(true);
		} else if (value === MATCH_RETIRE_ACTIONS.UNDO_RETIRE) {
			setShowUndoRetireModal(true);
		} else if (value === MATCH_RETIRE_ACTIONS.UNDO_FORFEIT) {
			setShowUndoForfeitModal(true);
		}
	};

	const handleBlur = () => {
		if (score === null) {
			setScore(0);
			onChange(0, '');
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		const isDigit = /^[0-9]$/.test(e.key);
		const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight'];

		if (allowedKeys.includes(e.key)) return;

		if (e.key === 'ArrowUp') {
			e.preventDefault();
			const newScore = Math.min((score ?? 0) + 1, GAME_SCORE_MAX);
			setScore(newScore);
			onChange(newScore, '');
			return;
		}

		if (e.key === 'ArrowDown') {
			e.preventDefault();
			const newScore = Math.max((score ?? 0) - 1, 0);
			setScore(newScore);
			onChange(newScore, '');
			return;
		}

		const currentVal = score === null ? '' : String(score);
		if (!isDigit || currentVal.length >= 2) {
			e.preventDefault();
		}
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		let val = e.target.value;

		if (val === '') {
			setScore(null);
			onChange(0, '');
			return;
		}

		if (!/^\d{1,2}$/.test(val)) return;

		if (val.length > 1 && val.startsWith('0')) {
			val = val.replace(/^0+/, '') || '0';
		}

		const parsed = parseInt(val, 10);
		if (parsed <= GAME_SCORE_MAX) {
			setScore(parsed);
			onChange(parsed, '');
		}
	};

	//const isGameWinner = typeof score === 'number' && typeof opponentScore === 'number' && score >= scoreFormat && score > opponentScore;

	return (
		<>
			<div className="flex w-auto sm:w-fit flex-col items-center font-medium ">
				<div className="mb-2 flex w-full items-center justify-center gap-1">
					{!isShowDropdown && <span className={clsx('text-sm text-gray-900', isLocked && 'text-gray-600')}>{gameNumber}</span>}
					{isShowDropdown ? (
						<MatchRetireDropdown
							options={getDropdownOptions(initialRetireStatus)}
							onSelect={(value) => handleRetireSelect(value)}
							placeholder={gameNumber}
							disabled={!permissionStore.gamedayAccess}
						/>
					) : null}
				</div>
				<input
					type="text"
					value={score === null ? '' : String(score)}
					inputMode="numeric"
					onBlur={handleBlur}
					onKeyDown={handleKeyDown}
					onChange={handleChange}
					disabled={isLocked}
					className={clsx(
						'rounded-lg border py-2 text-center text-base shadow-xs px-3  w-12 h-10',
						'[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
						'focus:outline-none focus:ring-2 focus:ring-warning-400',
						isLocked ? 'border-gray-300 bg-gray-200' : 'border-gray-300 bg-white',
						initialRetireStatus && 'border-warning-500 text-warning-500 focus:border-warning-500 focus:ring-2 focus:ring-warning-500',
						isFinalGame && 'text-lg font-bold',
						isMatchWinner && 'bg-green-600 text-white border-green-600'
					)}
				/>
				<p className="text-sm text-warning-500">{initialRetireStatus}</p>
			</div>
			{showRetireModal ? (
				<ConfirmRetireMatchModal
					setShowModal={setShowRetireModal}
					matchId={matchId}
					scoreFormat={scoreFormat}
					gameNumber={gameNumber}
					team={team}
					games={games}
				/>
			) : null}
			{showForfeitModal ? (
				<ConfirmForfeitMatchModal
					setShowModal={setShowForfeitModal}
					matchId={matchId}
					scoreFormat={scoreFormat}
					gameNumber={gameNumber}
					team={team}
				/>
			) : null}
			{showUndoRetireModal ? (
				<ConfirmUndoRetirementModal setShowModal={setShowUndoRetireModal} matchId={matchId} gameNumber={gameNumber} team={team} />
			) : null}
			{showUndoForfeitModal ? (
				<ConfirmUndoForfeitModal setShowModal={setShowUndoForfeitModal} matchId={matchId} gameNumber={gameNumber} team={team} />
			) : null}
		</>
	);
};
